import { slotNumberFromSlotName } from './constants';
import {
  CURRENT_PROJECT_VERSION,
  parsePartialVersionString,
  parseProjectVersionString,
  projectVersionToString,
  resolveDeployFrameworkVersionString,
} from './projectVersions';

/** Injected at deploy time only; not stored in editor buffer. */
const BPS_PROJECT_ID_MARKER = '__BPS_PROJECT_ID__=';
const BPS_PROJECT_NAME_MARKER = '__BPS_PROJECT_NAME__=';
const BPS_PROJECT_LAST_MODIFIED_UTC_MS_MARKER = '__BPS_PROJECT_LAST_MODIFIED_UTC_MS__=';
const BPS_PROJECT_VERSION_MARKER = '__BPS_PROJECT_VERSION__=';
const BPS_FRAMEWORK_VERSION_MARKER = '__BPS_FRAMEWORK_VERSION__=';
const BPS_PROJECT_FOOTER_MARKER = '__BPS_PROJECT_FOOTER__';

export const BPS_PROJECT_ID_COMMENT_PREFIX = `# ${BPS_PROJECT_ID_MARKER}`;
export const BPS_PROJECT_NAME_COMMENT_PREFIX = `# ${BPS_PROJECT_NAME_MARKER}`;
export const BPS_PROJECT_LAST_MODIFIED_UTC_MS_COMMENT_PREFIX = `# ${BPS_PROJECT_LAST_MODIFIED_UTC_MS_MARKER}`;
export const BPS_PROJECT_VERSION_COMMENT_PREFIX = `# ${BPS_PROJECT_VERSION_MARKER}`;
export const BPS_FRAMEWORK_VERSION_COMMENT_PREFIX = `# ${BPS_FRAMEWORK_VERSION_MARKER}`;
export const BPS_PROJECT_FOOTER_COMMENT_PREFIX = `# ${BPS_PROJECT_FOOTER_MARKER}`;

/** Compact deploy footer line: `# <value>` with no field marker (order is positional after the delimiter). */
export function formatBpsDeployMetadataValueCommentLine(value: string | number): string {
  return `# ${value}`;
}

const UUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
const BPS_COMPACT_DEPLOY_FOOTER_METADATA_LINE_COUNT = 5;

/**
 * TODO(pre-release): Remove legacy BPS deploy-metadata read-back support in this section.
 * Slot program metadata has not shipped to production; the parsers below exist only so QA/dev
 * can recover on-device Python from internal builds (leading marker headers, variable-length
 * compact footers, marker-based trailing footers). Before release, keep fixed-order 5-line
 * delimited EOF footers ({@link parseFixedOrderCompactBpsDeployMetadataLines}) only.
 */
/** Legacy leading deploy headers appended at most three metadata comment lines. */
const MAX_BPS_LEGACY_DEPLOY_FOOTER_LINES = 3;

export interface BpsProjectDeployMetadata {
  projectId?: string;
  projectName?: string;
  projectLastModifiedUtcMs?: number;
  projectVersion?: string;
  /** Parsed from deploy footers; ignored by {@link appendBpsProjectMetadataToDeployedPython}. */
  frameworkVersion?: string;
}

function normalizeProjectName(projectName: string | null | undefined): string | undefined {
  if (typeof projectName !== 'string') return undefined;
  const normalized = projectName.replace(/\r?\n/g, ' ').trim();
  return normalized || undefined;
}

function normalizeProjectLastModifiedUtcMs(projectLastModifiedUtcMs: number | string | null | undefined): number | undefined {
  const normalized = typeof projectLastModifiedUtcMs === 'string' ? Number(projectLastModifiedUtcMs.trim()) : projectLastModifiedUtcMs;
  if (typeof normalized !== 'number' || !Number.isInteger(normalized) || normalized <= 0) {
    return undefined;
  }
  return normalized;
}

function normalizeVersionString(value: string | null | undefined): string | undefined {
  if (typeof value !== 'string') return undefined;
  const parsed = parseProjectVersionString(value) ?? parsePartialVersionString(value);
  return parsed ? projectVersionToString(parsed) : undefined;
}

function normalizeStoredFrameworkVersion(value: string | null | undefined): string {
  return resolveDeployFrameworkVersionString(value);
}

export function parseProjectLastModifiedToUtcMs(lastModified: string | null | undefined): number | undefined {
  if (typeof lastModified !== 'string') return undefined;
  const parsed = Date.parse(lastModified);
  return Number.isFinite(parsed) ? parsed : undefined;
}

export function projectLastModifiedUtcMsToIsoString(projectLastModifiedUtcMs: number | undefined): string | undefined {
  if (typeof projectLastModifiedUtcMs !== 'number' || !Number.isFinite(projectLastModifiedUtcMs)) {
    return undefined;
  }
  return new Date(projectLastModifiedUtcMs).toISOString();
}

function isBpsDeployFooterDelimiterLine(line: string): boolean {
  return line.trim() === BPS_PROJECT_FOOTER_COMMENT_PREFIX;
}

/** TODO(pre-release): Remove with legacy marker-based deploy metadata. */
function isLegacyBpsDeployMetadataLine(line: string): boolean {
  return (
    line.startsWith(BPS_PROJECT_ID_COMMENT_PREFIX) ||
    line.startsWith(BPS_PROJECT_NAME_COMMENT_PREFIX) ||
    line.startsWith(BPS_PROJECT_LAST_MODIFIED_UTC_MS_COMMENT_PREFIX) ||
    line.startsWith(BPS_PROJECT_VERSION_COMMENT_PREFIX) ||
    line.startsWith(BPS_FRAMEWORK_VERSION_COMMENT_PREFIX)
  );
}

function isBpsDeployCompactMetadataCommentLine(line: string): boolean {
  const trimmed = line.trim();
  return trimmed.startsWith('#') && !isBpsDeployFooterDelimiterLine(line);
}

function isBpsDeployMetadataLine(line: string): boolean {
  return isLegacyBpsDeployMetadataLine(line) || isBpsDeployCompactMetadataCommentLine(line);
}

/**
 * BPS deploy footer at EOF: delimiter `# __BPS_PROJECT_FOOTER__` then compact or legacy metadata comment lines.
 * Scans upward from EOF through metadata until the footer delimiter (or a non-footer line).
 */
function findTrailingBpsDeployFooterLineRange(lines: string[]): { start: number; end: number } | null {
  let end = lines.length;
  while (end > 0 && lines[end - 1] === '') {
    end -= 1;
  }
  if (end === 0) return null;

  let scan = end;
  while (scan > 0) {
    const line = lines[scan - 1];
    if (isBpsDeployFooterDelimiterLine(line)) {
      return { start: scan - 1, end };
    }
    if (isBpsDeployMetadataLine(line)) {
      scan -= 1;
      continue;
    }
    break;
  }

  return null;
}

/**
 * Legacy deploy header at the start of the file (programs deployed before metadata moved to EOF).
 * TODO(pre-release): Remove leading-header scan; metadata never shipped with leading headers.
 */
function findLeadingBpsDeployFooterLineRange(lines: string[]): { start: number; end: number } | null {
  let start = 0;
  while (start < lines.length && lines[start] === '') {
    start += 1;
  }

  let end = start;
  let footerLineCount = 0;
  while (end < lines.length && footerLineCount < MAX_BPS_LEGACY_DEPLOY_FOOTER_LINES && isLegacyBpsDeployMetadataLine(lines[end])) {
    end += 1;
    footerLineCount += 1;
  }

  return footerLineCount > 0 ? { start, end } : null;
}

/** TODO(pre-release): Remove with legacy marker-based deploy metadata. */
function applyLegacyBpsDeployMetadataLine(metadata: BpsProjectDeployMetadata, line: string): void {
  const trimmed = line.trim();
  if (!trimmed.startsWith('#')) return;

  const rest = trimmed.slice(1).trim();
  if (rest.startsWith(BPS_PROJECT_ID_MARKER)) {
    const projectId = rest.slice(BPS_PROJECT_ID_MARKER.length).trim();
    if (UUID_REGEX.test(projectId)) {
      metadata.projectId = projectId;
    }
    return;
  }

  if (rest.startsWith(BPS_PROJECT_NAME_MARKER)) {
    const projectName = normalizeProjectName(rest.slice(BPS_PROJECT_NAME_MARKER.length));
    if (projectName) {
      metadata.projectName = projectName;
    }
    return;
  }

  if (rest.startsWith(BPS_PROJECT_LAST_MODIFIED_UTC_MS_MARKER)) {
    const projectLastModifiedUtcMs = normalizeProjectLastModifiedUtcMs(rest.slice(BPS_PROJECT_LAST_MODIFIED_UTC_MS_MARKER.length));
    if (projectLastModifiedUtcMs !== undefined) {
      metadata.projectLastModifiedUtcMs = projectLastModifiedUtcMs;
    }
    return;
  }

  if (rest.startsWith(BPS_PROJECT_VERSION_MARKER)) {
    const projectVersion = normalizeVersionString(rest.slice(BPS_PROJECT_VERSION_MARKER.length));
    if (projectVersion) {
      metadata.projectVersion = projectVersion;
    }
    return;
  }

  if (rest.startsWith(BPS_FRAMEWORK_VERSION_MARKER)) {
    const frameworkVersion = normalizeStoredFrameworkVersion(rest.slice(BPS_FRAMEWORK_VERSION_MARKER.length));
    metadata.frameworkVersion = frameworkVersion;
  }
}

function parseFixedOrderCompactBpsDeployMetadataLines(values: string[]): BpsProjectDeployMetadata {
  const metadata: BpsProjectDeployMetadata = {};

  const frameworkVersion = normalizeVersionString(values[0]);
  if (frameworkVersion) {
    metadata.frameworkVersion = normalizeStoredFrameworkVersion(frameworkVersion);
  }

  const projectVersion = normalizeVersionString(values[1]);
  if (projectVersion) {
    metadata.projectVersion = projectVersion;
  }

  const projectId = values[2]?.trim();
  if (projectId && UUID_REGEX.test(projectId)) {
    metadata.projectId = projectId;
  }

  const projectName = normalizeProjectName(values[3]);
  if (projectName) {
    metadata.projectName = projectName;
  }

  const projectLastModifiedUtcMs = normalizeProjectLastModifiedUtcMs(values[4]);
  if (projectLastModifiedUtcMs !== undefined) {
    metadata.projectLastModifiedUtcMs = projectLastModifiedUtcMs;
  }

  return metadata;
}

/** TODO(pre-release): Remove with legacy variable-length compact footers (pre fixed-order 5-line format). */
function parseLegacyCompactBpsDeployMetadataLines(values: string[]): BpsProjectDeployMetadata {
  const compactValues = values.filter((value) => value.length > 0);

  const metadata: BpsProjectDeployMetadata = {};
  let index = 0;

  if (index < compactValues.length) {
    const frameworkVersion = normalizeVersionString(compactValues[index]);
    if (frameworkVersion) {
      metadata.frameworkVersion = normalizeStoredFrameworkVersion(frameworkVersion);
      index += 1;
    }
  }

  if (index < compactValues.length) {
    const projectVersion = normalizeVersionString(compactValues[index]);
    if (projectVersion) {
      metadata.projectVersion = projectVersion;
      index += 1;
    }
  }

  if (index < compactValues.length && UUID_REGEX.test(compactValues[index])) {
    metadata.projectId = compactValues[index];
    index += 1;
  }

  if (index < compactValues.length) {
    const projectName = normalizeProjectName(compactValues[index]);
    if (projectName) {
      metadata.projectName = projectName;
      index += 1;
    }
  }

  if (index < compactValues.length) {
    const projectLastModifiedUtcMs = normalizeProjectLastModifiedUtcMs(compactValues[index]);
    if (projectLastModifiedUtcMs !== undefined) {
      metadata.projectLastModifiedUtcMs = projectLastModifiedUtcMs;
    }
  }

  return metadata;
}

function parseCompactBpsDeployMetadataLines(lines: string[]): BpsProjectDeployMetadata {
  const values = lines.filter((line) => !isBpsDeployFooterDelimiterLine(line)).map((line) => line.trim().slice(1).trim());
  const hasFixedOrderFooter =
    values.length >= BPS_COMPACT_DEPLOY_FOOTER_METADATA_LINE_COUNT && normalizeVersionString(values[0]) && normalizeVersionString(values[1]);

  if (hasFixedOrderFooter) {
    return parseFixedOrderCompactBpsDeployMetadataLines(values);
  }

  // TODO(pre-release): Drop fallback; all shipped footers use fixed-order 5-line compact values.
  return parseLegacyCompactBpsDeployMetadataLines(values);
}

function parseBpsDeployMetadataLines(metadataLines: string[]): BpsProjectDeployMetadata {
  const contentLines = metadataLines.filter((line) => !isBpsDeployFooterDelimiterLine(line));
  // TODO(pre-release): Remove marker-line branch; only fixed-order compact footers will remain.
  if (contentLines.some(isLegacyBpsDeployMetadataLine)) {
    const metadata: BpsProjectDeployMetadata = {};
    for (const line of contentLines) {
      applyLegacyBpsDeployMetadataLine(metadata, line);
    }
    return metadata;
  }

  return parseCompactBpsDeployMetadataLines(contentLines);
}

/**
 * Extracts BPS deploy metadata from Python read back from a device slot.
 *
 * Reads one delimited footer section at EOF (current deploy format) and, when present, one legacy leading metadata section at the file start.
 * TODO(pre-release): Stop scanning leading legacy headers once {@link findLeadingBpsDeployFooterLineRange} is removed.
 */
export function parseBpsProjectMetadataFromPythonSource(source: string): BpsProjectDeployMetadata {
  if (!source || typeof source !== 'string') return {};

  const lines = source.split(/\r?\n/);
  const leadingRange = findLeadingBpsDeployFooterLineRange(lines);
  const trailingRange = findTrailingBpsDeployFooterLineRange(lines);

  const metadata: BpsProjectDeployMetadata = {};

  if (leadingRange) {
    Object.assign(metadata, parseBpsDeployMetadataLines(lines.slice(leadingRange.start, leadingRange.end)));
  }
  if (trailingRange && (!leadingRange || trailingRange.start >= leadingRange.end)) {
    Object.assign(metadata, parseBpsDeployMetadataLines(lines.slice(trailingRange.start, trailingRange.end)));
  }

  return metadata;
}

/**
 * Parses BPS project id from deploy metadata comments in Python source.
 */
export function parseBpsProjectIdFromPythonSource(source: string): string | null {
  return parseBpsProjectMetadataFromPythonSource(source).projectId ?? null;
}

/**
 * Removes BPS deploy footer/header comments from Python read back from a device slot.
 *
 * Strips at most one delimited footer section from EOF and one legacy header section from the file start.
 * TODO(pre-release): Stop stripping leading legacy headers once {@link findLeadingBpsDeployFooterLineRange} is removed.
 */
export function stripBpsProjectMetadataFromDeployedPython(source: string): string {
  const lines = source.split(/\r?\n/);
  const leadingRange = findLeadingBpsDeployFooterLineRange(lines);
  const trailingRange = findTrailingBpsDeployFooterLineRange(lines);

  let bodyStart = 0;
  let bodyEnd = lines.length;

  if (leadingRange) {
    bodyStart = leadingRange.end;
  }

  if (trailingRange && trailingRange.start >= bodyStart) {
    bodyEnd = trailingRange.start;
  }

  const bodyLines = lines.slice(bodyStart, bodyEnd);
  while (bodyLines.length > 0 && bodyLines[bodyLines.length - 1] === '') {
    bodyLines.pop();
  }

  return bodyLines.length === 0 ? '' : `${bodyLines.join('\n')}\n`;
}

/**
 * Backward-compatible alias for stripping deploy-only metadata from Python read back from a device slot.
 */
export function stripBpsProjectIdFromDeployedPython(source: string): string {
  return stripBpsProjectMetadataFromDeployedPython(source);
}

export interface SaveSlotLike {
  isEmpty?: boolean;
  programTitle?: string;
  bpsProjectId?: string;
  bpsProjectLastModifiedUtcMs?: number;
  deviceFilename?: string;
}

export function getPositiveSaveSlotNumber(slot: unknown): number | null {
  const slotNumber = typeof slot === 'string' ? Number(slot.trim()) : Number(slot);
  return Number.isInteger(slotNumber) && slotNumber > 0 ? slotNumber : null;
}

export function getValidSaveSlotNumber(slot: unknown, maxSlot = 8): number | null {
  const slotNumber = getPositiveSaveSlotNumber(slot);
  return slotNumber !== null && slotNumber <= maxSlot ? slotNumber : null;
}

function isEmptyPlaceholderName(name: string | undefined): boolean {
  if (!name) return false;
  const normalized = String(name).trim().split('/').pop()?.split('\\').pop()?.toLowerCase() ?? '';
  return normalized === 'empty.py';
}

/**
 * Human-friendly label for slot program names.
 * Keeps internal metadata unchanged, but hides a trailing ".py" in UI labels.
 */
export function getSaveSlotDisplayProgramName(programTitle: string | undefined): string {
  if (!programTitle) return '';
  const trimmed = String(programTitle).trim();
  if (!trimmed) return '';
  return trimmed.replace(/\.py$/i, '');
}

/**
 * Returns true when slot metadata indicates no deployed program content.
 */
export function isSaveSlotEmpty(slot: SaveSlotLike | null | undefined): boolean {
  if (!slot) return true;
  if (slot.isEmpty) return true;
  if (isEmptyPlaceholderName(slot.programTitle) || isEmptyPlaceholderName(slot.deviceFilename)) return true;
  const hasProgram = !!(slot.programTitle && String(slot.programTitle).trim()) || !!slot.deviceFilename;
  return !hasProgram;
}

/**
 * Derives the device filename that BPS would produce when deploying a project.
 * Must mirror the logic in robot-service.js sendProgram().
 */
export function projectNameToDeviceFilename(projectName: string): string {
  const baseName = projectName.replace(/[^A-Za-z0-9_-]/g, '_') || 'program';
  return baseName.endsWith('.py') ? baseName : `${baseName}.py`;
}

/**
 * Returns true when slot metadata proves it contains the same deployed revision
 * as the loaded project. Falls back to id-only matching when the loaded project
 * does not have a usable lastModified timestamp.
 */
export function saveSlotMatchesProject(
  slot: SaveSlotLike | null | undefined,
  loadedProjectId: string | null | undefined,
  loadedProjectLastModifiedUtcMs?: number
): boolean {
  if (!slot || !loadedProjectId || slot.bpsProjectId !== loadedProjectId) {
    return false;
  }

  if (loadedProjectLastModifiedUtcMs === undefined) {
    return true;
  }

  return slot.bpsProjectLastModifiedUtcMs === loadedProjectLastModifiedUtcMs;
}

/**
 * Chooses the best save slot for a project.
 * Priority:
 * 1) exact project revision match
 * 2) same project id (when no exact revision match exists)
 * 3) first empty slot
 * 4) slot 1
 */
export function getPreferredSaveSlot(
  slotStorage: Record<string, SaveSlotLike | null | undefined> | null | undefined,
  loadedProjectId?: string | null,
  loadedProjectLastModifiedUtcMs?: number
): number {
  if (!slotStorage) return 1;
  const entries = Object.entries(slotStorage).filter((e): e is [string, SaveSlotLike] => Boolean(e[1]));
  if (entries.length === 0) return 1;

  if (loadedProjectId) {
    const ownRevisionEntry = entries.find(([, s]) => saveSlotMatchesProject(s, loadedProjectId, loadedProjectLastModifiedUtcMs));
    if (ownRevisionEntry) return slotNumberFromSlotName(ownRevisionEntry[0]);
    const ownProjectEntry = entries.find(([, s]) => s?.bpsProjectId === loadedProjectId);
    if (ownProjectEntry) return slotNumberFromSlotName(ownProjectEntry[0]);
  }

  const emptyEntry = entries.find(([, s]) => isSaveSlotEmpty(s));
  if (emptyEntry) return slotNumberFromSlotName(emptyEntry[0]);

  return 1;
}

/**
 * Returns true if selecting this occupied slot may overwrite another project's program and needs confirmation.
 */
export function saveSlotNeedsReplaceConfirmation(
  slot: SaveSlotLike | null | undefined,
  loadedProjectId: string | null | undefined,
  loadedProjectLastModifiedUtcMs?: number
): boolean {
  if (isSaveSlotEmpty(slot)) return false;
  if (!slot) return false;

  if (!loadedProjectId) return true;

  if (saveSlotMatchesProject(slot, loadedProjectId, loadedProjectLastModifiedUtcMs)) return false;

  return true;
}

/**
 * Appends deploy-only metadata comments after user Python so payloads stay executable-first and can still be parsed on read-back.
 *
 * Footer format:
 * ```
 * # __BPS_PROJECT_FOOTER__
 * # <framework-version>
 * # <project-version>
 * # <project-id or blank>
 * # <project-name or blank>
 * # <last-modified-utc-ms or blank>
 * ```
 *
 * Framework version comes from deploy metadata when available and otherwise falls back to the app's current framework version.
 */
export function appendBpsProjectMetadataToDeployedPython(
  pythonScript: string,
  { projectId, projectName, projectLastModifiedUtcMs, projectVersion, frameworkVersion }: BpsProjectDeployMetadata
): string {
  const normalizedProjectId = typeof projectId === 'string' && UUID_REGEX.test(projectId.trim()) ? projectId.trim() : undefined;
  const normalizedProjectName = normalizeProjectName(projectName);
  const normalizedProjectLastModifiedUtcMs = normalizeProjectLastModifiedUtcMs(projectLastModifiedUtcMs);
  const normalizedProjectVersion = normalizeVersionString(projectVersion);
  const shouldAppendFooter =
    normalizedProjectId !== undefined ||
    normalizedProjectName !== undefined ||
    normalizedProjectLastModifiedUtcMs !== undefined ||
    normalizedProjectVersion !== undefined;

  if (!shouldAppendFooter) return pythonScript;

  const normalizedFrameworkVersion = resolveDeployFrameworkVersionString(frameworkVersion);
  const resolvedProjectVersion = normalizedProjectVersion ?? projectVersionToString(CURRENT_PROJECT_VERSION);
  const metadataLines = [
    formatBpsDeployMetadataValueCommentLine(normalizedFrameworkVersion),
    formatBpsDeployMetadataValueCommentLine(resolvedProjectVersion),
    formatBpsDeployMetadataValueCommentLine(normalizedProjectId ?? ''),
    formatBpsDeployMetadataValueCommentLine(normalizedProjectName ?? ''),
    formatBpsDeployMetadataValueCommentLine(normalizedProjectLastModifiedUtcMs ?? ''),
  ];

  const normalizedPythonScript = pythonScript.endsWith('\n') ? pythonScript : `${pythonScript}\n`;
  const footerLines = [BPS_PROJECT_FOOTER_COMMENT_PREFIX, ...metadataLines];

  return `${normalizedPythonScript}${footerLines.join('\n')}\n`;
}

/** @deprecated Use {@link appendBpsProjectMetadataToDeployedPython}; name retained for older imports. */
export function prependBpsProjectMetadataToDeployedPython(pythonScript: string, metadata: BpsProjectDeployMetadata): string {
  return appendBpsProjectMetadataToDeployedPython(pythonScript, metadata);
}

/**
 * Appends deploy-only project id metadata when name / last-modified are unavailable.
 */
export function appendBpsProjectIdToDeployedPython(pythonScript: string, projectId: string): string {
  return appendBpsProjectMetadataToDeployedPython(pythonScript, { projectId });
}

/** @deprecated Use {@link appendBpsProjectIdToDeployedPython}; name retained for older imports. */
export function prependBpsProjectIdToDeployedPython(pythonScript: string, projectId: string): string {
  return appendBpsProjectIdToDeployedPython(pythonScript, projectId);
}
